import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { getFullscreenElement, getFullscreenMethod, getExitFullscreenMethod } from '@/utils/fullscreenApi';
import { isMobileDevice, isIOSDevice, isIOSSafari, isMobileChrome, isPWA } from '@/utils/deviceDetection';
import { setupMobileViewport, createIOSFullscreenExperience } from '@/utils/mobileFullscreen';

export const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!getFullscreenElement();
      setIsFullscreen(isCurrentlyFullscreen);
      
      console.log('🔍 [FULLSCREEN] State changed:', {
        isFullscreen: isCurrentlyFullscreen,
        element: getFullscreenElement(),
        isMobile: isMobileDevice(),
        isMobileChrome: isMobileChrome(),
        userAgent: navigator.userAgent
      });
    };

    // Add all possible fullscreen change events
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'msfullscreenchange'];
    events.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });
    };
  }, []);

  const requestFullscreen = useCallback(async () => {
    try {
      console.log('🔍 [FULLSCREEN] Attempting fullscreen:', {
        isMobile: isMobileDevice(),
        isIOS: isIOSDevice(),
        isPWA: isPWA(),
        userAgent: navigator.userAgent,
        currentElement: getFullscreenElement()
      });

      // Mobile-first approach: always apply mobile optimizations
      if (isMobileDevice()) {
        setupMobileViewport();
        
        // For iOS, focus on viewport optimization since fullscreen API doesn't work
        if (isIOSDevice()) {
          createIOSFullscreenExperience();
          setIsFullscreen(true); // Simulate fullscreen state for UI feedback
          toast({
            title: "Immersive Mode Active",
            description: "For true fullscreen, add to home screen via Safari share menu",
            duration: 4000,
          });
          return;
        }
        
        // For PWA or other mobile, try viewport first then native API
        if (isPWA()) {
          setIsFullscreen(true);
          console.log('🔍 [FULLSCREEN] PWA immersive mode activated');
          return;
        }
      }

      // Try native fullscreen API (works on desktop and some mobile browsers)
      const fullscreenMethod = getFullscreenMethod();
      if (!getFullscreenElement() && fullscreenMethod) {
        await fullscreenMethod();
        console.log('🔍 [FULLSCREEN] Native fullscreen activated');
      } else if (isMobileDevice()) {
        // Fallback: simulate fullscreen for mobile
        setIsFullscreen(true);
        toast({
          title: "Immersive Mode",
          description: "Browser UI minimized for better experience",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('🔍 [FULLSCREEN] Request failed:', error);
      
      // Always provide fallback for mobile
      if (isMobileDevice()) {
        setupMobileViewport();
        setIsFullscreen(true);
        toast({
          title: "Mobile Optimized",
          description: "Screen optimized for mobile viewing",
        });
      }
    }
  }, [hasUserInteracted]);

  const exitFullscreen = useCallback(async () => {
    try {
      const exitMethod = getExitFullscreenMethod();
      
      if (getFullscreenElement() && exitMethod) {
        await exitMethod();
        console.log('🔍 [FULLSCREEN] Successfully exited fullscreen');
      } else if (isMobileDevice()) {
        // For mobile fallback, just reset the state
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('🔍 [FULLSCREEN] Exit failed:', error);
      // Reset state on error
      setIsFullscreen(false);
    }
  }, []);

  const markUserInteraction = useCallback(() => {
    setHasUserInteracted(true);
  }, []);

  const isSupported = !!getFullscreenMethod();

  return {
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
    markUserInteraction,
    isSupported,
    isMobileDevice: isMobileDevice(),
    isIOSDevice: isIOSDevice(),
    isIOSSafari: isIOSSafari(),
    isMobileChrome: isMobileChrome(),
    isPWA: isPWA()
  };
};